import { all, put, takeLatest, takeEvery } from 'redux-saga/effects';
import {
  addBookingToList,
  addClosureObjectToList,
  createBooking,
  createClosureObject,
  getBookingList,
  getChessObjects,
  getClosureObjectsList,
  getTotalPrice,
  removeBooking,
  removeBookingFromList,
  removeClosureObject,
  removeClosureObjectFromList,
  setBookingList,
  setBookingListError,
  setChessObjectsList,
  setClosureObjectsList,
  setPriceError,
  setTotalPrice,
  updateBookingInList,
  updateCurrentBooking,
  updateCurrentClosure,
  updateViewedStatus,
} from './../reducers/bookingSlice';
import { PayloadAction } from '@reduxjs/toolkit';
import { ApiResponse } from 'apisauce';
import API from '../api';
import {
  BookingType,
  ChessObjectsResponse,
  UpdateBookingType,
  UpdateBookingPayloadData,
  UpdateClosureObjectType,
  PriceResponseType,
  PriceRequestType,
} from '../types/bookingTypes';
import callCheckingAuth from './callCheckingAuth';
import {
  setLoadersData,
  setMessage,
  setModalWindowData,
} from '../reducers/pageSlice';
import {
  LoaderTypes,
  MessageTypes,
  ModalWindowTypes,
} from '../../utils/@globalTypes';
import {
  ErrorResponseData,
  PayloadDataWithId,
  ResponseData,
} from '../types/@types';
import {
  getBookingError,
} from '../../utils/functions';
import {
  isMinimumReservationDurationError,
} from '../../components/Booking/functions';


function* getChessObjectListWorker() {
  yield put(setLoadersData({ type: LoaderTypes.BOOKING_PAGE, value: true }));
  const { ok, data, status }: ApiResponse<ChessObjectsResponse> =
    yield callCheckingAuth(API.getCheckerboardList);

  if (data && ok) {
    yield put(setChessObjectsList(data.results));
  } else {
    if (status) {
      yield put(
        setMessage({
          status: MessageTypes.ERROR,
          message: 'Ошибка получения данных об объектах',
          code: status,
        }),
      );
    }
  }
  yield put(setLoadersData({ type: LoaderTypes.BOOKING_PAGE, value: false }));
}
function* getBookingListWorker(
  action: PayloadAction<{ start: string; end: string }>,
) {
  yield put(setLoadersData({ type: LoaderTypes.BOOKING_PAGE, value: true }));

  const { ok, data, status }: ApiResponse<BookingType[]> =
    yield callCheckingAuth(API.getBookingList, action.payload);

  if (data && ok) {
    yield put(setBookingList(data));
  } else {
    yield put(setBookingListError('Не удалось загрузить внутренние брони.'));
    if (status) {
      yield put(
        setMessage({
          status: MessageTypes.ERROR,
          message: 'Ошибка получения списка броней',
          code: status,
        }),
      );
    }
  }
  yield put(setLoadersData({ type: LoaderTypes.BOOKING_PAGE, value: false }));
}

function* createBookingWorker(action: PayloadAction<UpdateBookingType>) {
  yield put(setLoadersData({ type: LoaderTypes.BOOKING_PAGE, value: true }));

  const { ok, data, status }: ApiResponse<BookingType, ErrorResponseData> =
    yield callCheckingAuth(API.createBookingRequest, action.payload);

  if (ok && status && data) {
    yield put(addBookingToList(data));
    yield put(setTotalPrice(null))
    yield put(setModalWindowData({ type: ModalWindowTypes.CLOSE }));
    yield put(
      setMessage({
        status: MessageTypes.POSITIVE,
        message: 'Бронь сохранена',
        code: status,
      }),
    );
  } else {
    if (status) {
      if (data && !ok && status === 400) {
        const text = isMinimumReservationDurationError(data)
          ? 'Бронь должна быть длительностью не менее 12 часов'
          : 'Объект недоступен в указанные даты';
        yield put(
          setMessage({
            status: MessageTypes.WARNING,
            message: text,
            code: 401,
          }),
        );
      } else {
        yield put(
          setMessage({
            status: MessageTypes.ERROR,
            message: 'Ошибка сохранения',
            code: status,
          }),
        );
      }
    }
  }
  yield put(setLoadersData({ type: LoaderTypes.BOOKING_PAGE, value: false }));
  return status;
}

function* removeBookingWorker(action: PayloadAction<number>) {
  yield put(setLoadersData({ type: LoaderTypes.BOOKING_PAGE, value: true }));
  const { ok, status }: ApiResponse<undefined> = yield callCheckingAuth(
    API.removeBookingRequest,
    action.payload,
  );

  if (status && ok) {
    yield put(removeBookingFromList(action.payload));
    yield put(
      setMessage({
        status: MessageTypes.POSITIVE,
        message: 'Бронь удалена',
        code: 200,
      }),
    );
    yield put(setModalWindowData({ type: ModalWindowTypes.CLOSE }));
  } else {
    if (status && !ok) {
      yield put(
        setMessage({
          status: MessageTypes.ERROR,
          message: 'Не удалось удалить бронь',
          code: status,
        }),
      );
    } else {
      yield put(
        setMessage({
          status: MessageTypes.ERROR,
          message: 'Ошибка удаления ',
          code: 500,
        }),
      );
    }
  }
  yield put(setLoadersData({ type: LoaderTypes.BOOKING_PAGE, value: false }));
}

function* updateBookingWorker(action: PayloadAction<UpdateBookingPayloadData>) {
  yield put(
    setLoadersData({
      type: LoaderTypes.CREATE_RENTAL_OBJECT_POPUP,
      value: true,
    }),
  );
  const { id, data } = action.payload;

  const {
    ok,
    data: responseData,
    status,
  }: ApiResponse<BookingType, ErrorResponseData> = yield callCheckingAuth(
    API.updateBookingRequest,
    id,
    data,
  );

  if (ok && status && responseData) {
    yield put(setModalWindowData({ type: ModalWindowTypes.CLOSE }));
    yield put(updateBookingInList(responseData));
    yield put(setTotalPrice(null))
    yield put(
      setMessage({
        status: MessageTypes.POSITIVE,
        message: 'Изменения сохранены',
        code: status,
      }),
    );
  } else {
    if (status) {
      if (responseData && !ok && status === 400) {
        const text = isMinimumReservationDurationError(responseData)
          ? 'Бронь должна быть длительностью не менее 12 часов'
          : 'Объект недоступен в указанные даты';
        yield put(
          setMessage({
            status: MessageTypes.WARNING,
            message: text,
            code: 401,
          }),
        );
      } else {
        yield put(
          setMessage({
            status: MessageTypes.ERROR,
            message: 'Ошибка сохранения',
            code: status,
          }),
        );
      }
    }
  }

  yield put(
    setLoadersData({
      type: LoaderTypes.CREATE_RENTAL_OBJECT_POPUP,
      value: false,
    }),
  );
}
function* updateViewedStatusWorker(action: PayloadAction<number>) {
  const id = action.payload;
  const {
    ok,
    data: responseData,
    status,
  }: ApiResponse<BookingType, ErrorResponseData> = yield callCheckingAuth(
    API.updateViewBookingRequest,
    id,
    {
      viewed: true,
    },
  );
  // if (!ok) console.log('Ошибка сохранения статуса просмотра');
}

function* createClosureObjectWorker(
  action: PayloadAction<UpdateClosureObjectType>,
) {
  yield put(setLoadersData({ type: LoaderTypes.BOOKING_PAGE, value: true }));

  const {
    ok,
    data,
    status,
  }: ApiResponse<UpdateClosureObjectType, ErrorResponseData> =
    yield callCheckingAuth(API.createClosureObject, action.payload);

  if (ok && status && data) {
    yield put(addClosureObjectToList(data));
    yield put(setModalWindowData({ type: ModalWindowTypes.CLOSE }));
    yield put(
      setMessage({
        status: MessageTypes.POSITIVE,
        message: 'Данные сохранены',
        code: status,
      }),
    );
  } else {
    if (status) {
      if (data && !ok && status === 400) {
        const text = 'Ошибка сохранения, объект забронирован на указанные даты';
        yield put(
          setMessage({
            status: MessageTypes.WARNING,
            message: text,
            code: 401,
          }),
        );
      } else {
        yield put(
          setMessage({
            status: MessageTypes.ERROR,
            message: 'Ошибка сохранения',
            code: status,
          }),
        );
      }
    }
  }
  yield put(setLoadersData({ type: LoaderTypes.BOOKING_PAGE, value: false }));
  return status;
}

function* getClosureObjectsListWorker() {
  yield put(setLoadersData({ type: LoaderTypes.BOOKING_PAGE, value: true }));
  const {
    ok,
    data,
    status,
  }: ApiResponse<UpdateClosureObjectType[]> =
    yield callCheckingAuth(API.getListClosureObjects);

  if (data && ok) {
    yield put(setClosureObjectsList(data));
  } else {
    if (status) {
      yield put(
        setMessage({
          status: MessageTypes.ERROR,
          message: 'Ошибка получения данных',
          code: status,
        }),
      );
    }
  }
  yield put(setLoadersData({ type: LoaderTypes.BOOKING_PAGE, value: false }));
}

function* removeClosureObjectWorker(action: PayloadAction<number>) {
  yield put(setLoadersData({ type: LoaderTypes.BOOKING_PAGE, value: true }));
  const { ok, status }: ApiResponse<undefined> = yield callCheckingAuth(
    API.removeClosureObject,
    action.payload,
  );

  if (status && ok) {
    yield put(removeClosureObjectFromList(action.payload));
    yield put(
      setMessage({
        status: MessageTypes.POSITIVE,
        message: 'Запись удалена',
        code: 200,
      }),
    );
    yield put(setModalWindowData({ type: ModalWindowTypes.CLOSE }));
  } else {
    if (status && !ok) {
      yield put(
        setMessage({
          status: MessageTypes.ERROR,
          message: 'Не удалось удалить запись',
          code: status,
        }),
      );
    } else {
      yield put(
        setMessage({
          status: MessageTypes.ERROR,
          message: 'Ошибка удаления ',
          code: 500,
        }),
      );
    }
  }
  yield put(setLoadersData({ type: LoaderTypes.BOOKING_PAGE, value: false }));
}
function* updateClosureWorker(
  action: PayloadAction<PayloadDataWithId<UpdateClosureObjectType>>,
) {
  yield put(setLoadersData({ type: LoaderTypes.BOOKING_PAGE, value: true }));
  const { id, data } = action.payload;

  const {
    ok,
    data: responseData,
    status,
  }: ApiResponse<
    UpdateClosureObjectType,
    ErrorResponseData
  > = yield callCheckingAuth(API.updateClosureObject, id, data);

  if (ok && status) {
    yield put(setModalWindowData({ type: ModalWindowTypes.CLOSE }));
    yield put(
      setMessage({
        status: MessageTypes.POSITIVE,
        message: 'Изменения сохранены',
        code: status,
      }),
    );
    const {
      ok: listOk,
      data: listResponse,
      status: listStatus,
    }: ApiResponse<
      ResponseData<UpdateClosureObjectType[]>
    > = yield put(getClosureObjectsList({}));   
  } else {
    if (status) {
      if (responseData && !ok && status === 400) {
        const text = 'Проверьте наличие броней на указанные даты';
        yield put(
          setMessage({
            status: MessageTypes.WARNING,
            message: text,
            code: 401,
          }),
        );
      } else {
        yield put(
          setMessage({
            status: MessageTypes.ERROR,
            message: 'Ошибка сохранения',
            code: status,
          }),
        );
      }
    }
  }
  yield put(setLoadersData({ type: LoaderTypes.BOOKING_PAGE, value: false }));
}

function* getTotalPriceWorker(action: PayloadAction<PriceRequestType>) {
  yield put(setLoadersData({ type: LoaderTypes.BOOKING_PAGE, value: true }));
  const { ok, data, status }: ApiResponse<PriceResponseType> =
    yield callCheckingAuth(API.getTotalPriceRequest, action.payload);

  if (data && ok) {
    yield put(setTotalPrice(data));
  } else {
    if (status === 400 && data) {
      const message: string = getBookingError(data);
      yield put(
        setPriceError(message)
        // setMessage({
        //   status: MessageTypes.ERROR,
        //   message: message,
        //   code: 401,
        // }),
      );
    } else {
      if (status)
        yield put(
          setMessage({
            status: MessageTypes.ERROR,
            message: 'Ошибка получения данных',
            code: 401,
          }),
        );
    }
  }
  yield put(setLoadersData({ type: LoaderTypes.BOOKING_PAGE, value: false }));
}

export default function* bookingSaga() {
  yield all([
    takeLatest(getChessObjects, getChessObjectListWorker),
    takeEvery(getBookingList, getBookingListWorker),
    takeLatest(createBooking, createBookingWorker),
    takeLatest(removeBooking, removeBookingWorker),
    takeLatest(updateCurrentBooking, updateBookingWorker),
    takeLatest(updateViewedStatus, updateViewedStatusWorker),
    takeLatest(createClosureObject, createClosureObjectWorker),
    takeLatest(getClosureObjectsList, getClosureObjectsListWorker),
    takeLatest(removeClosureObject, removeClosureObjectWorker),
    takeLatest(updateCurrentClosure, updateClosureWorker),
    takeLatest(getTotalPrice, getTotalPriceWorker),
  ]);
}
